import { useState, useCallback, useMemo } from 'react';
import type { QuestionFilters, QuestionStatus, QuestionSource } from '@/types';

interface FilterState extends QuestionFilters {
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const DEFAULT_STATE: FilterState = {
  page: 1,
  pageSize: 20,
  sortBy: 'timestamp',
  sortOrder: 'desc',
};

export function useFilters(initialState: Partial<FilterState> = {}) {
  const [state, setState] = useState<FilterState>({
    ...DEFAULT_STATE,
    ...initialState,
  });

  const setStatus = useCallback((status: QuestionStatus[]) => {
    setState((prev) => ({ ...prev, status, page: 1 }));
  }, []);

  const setSource = useCallback((source: QuestionSource[]) => {
    setState((prev) => ({ ...prev, source, page: 1 }));
  }, []);

  const setDateRange = useCallback((dateFrom?: string, dateTo?: string) => {
    setState((prev) => ({ ...prev, dateFrom, dateTo, page: 1 }));
  }, []);

  const setSearch = useCallback((search?: string) => {
    setState((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const setCategory = useCallback((category?: string) => {
    setState((prev) => ({ ...prev, category, page: 1 }));
  }, []);

  const setGroupName = useCallback((groupName?: string) => {
    setState((prev) => ({ ...prev, groupName, page: 1 }));
  }, []);

  const setAssignedDoctor = useCallback((assignedDoctor?: string) => {
    setState((prev) => ({ ...prev, assignedDoctor, page: 1 }));
  }, []);

  const setPriority = useCallback((priority?: string) => {
    setState((prev) => ({ ...prev, priority, page: 1 }));
  }, []);

  const setFavorite = useCallback((isFavorite?: boolean) => {
    setState((prev) => ({ ...prev, isFavorite, page: 1 }));
  }, []);

  const setTags = useCallback((tags?: string[]) => {
    setState((prev) => ({ ...prev, tags, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setState((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setState((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const setSort = useCallback((sortBy: string, sortOrder: 'asc' | 'desc') => {
    setState((prev) => ({ ...prev, sortBy, sortOrder }));
  }, []);

  const resetFilters = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  const filters = useMemo<QuestionFilters>(
    () => ({
      status: state.status,
      source: state.source,
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
      search: state.search,
      category: state.category,
      groupName: state.groupName,
      tags: state.tags,
      answeredBy: state.answeredBy,
      assignedDoctor: state.assignedDoctor,
      priority: state.priority,
      isFavorite: state.isFavorite,
    }),
    [state]
  );

  const hasActiveFilters = useMemo(() => {
    return !!(
      state.status?.length ||
      state.source?.length ||
      state.dateFrom ||
      state.dateTo ||
      state.search ||
      state.category ||
      state.groupName ||
      state.tags?.length ||
      state.assignedDoctor ||
      state.priority ||
      state.isFavorite
    );
  }, [state]);

  return {
    filters,
    page: state.page,
    pageSize: state.pageSize,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
    hasActiveFilters,
    setStatus,
    setSource,
    setDateRange,
    setSearch,
    setCategory,
    setGroupName,
    setAssignedDoctor,
    setPriority,
    setFavorite,
    setTags,
    setPage,
    setPageSize,
    setSort,
    resetFilters,
  };
}
